import React, { useRef, useState, useCallback, useMemo } from "react";
import { Box, Container, Snackbar, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SaveToListModal from "../Components/SaveToListModal";
import { ShortcutsModal } from "../Components/ShortcutsModal";
import { useKeyboardShortcuts } from "../utils/useKeyboardShortcuts";
import { apiScrapeUrl } from "../utils/useDb";
import {
  useSongLoader,
  useSongFullscreen,
  useSongAutoScroll,
  useSongPagination,
  SongHeader,
  SongMetadataChips,
  SongSavedListsBar,
  SongSplitView,
} from "./Song";

import { SongContent } from "./Song/components/SongContentToggles";
import { useUser } from "../utils/useUser";
import { DEFAULT_PAGE_TITLE, formatSongTitle, useDocumentTitle } from "../utils/titleUtils";


export default function SongPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const songId = parseInt(params.get("id") || "0", 10);

  // Local UI state
  const [copied, setCopied] = useState<boolean>(false);
  const [fontChange, setFontChange] = useState<number>(0);
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const [showSplitIndicators, setShowSplitIndicators] = useState<boolean>(true);
  const toggleSplitIndicators = useCallback(() => {
    setShowSplitIndicators((prev) => !prev);
  }, []);

  const [visibleContent, setVisibleContent] = useState<SongContent[]>(["tabs", "chords", "lyrics"]);
  const increaseFont = useCallback(() => setFontChange((value) => value + 1), []);
  const decreaseFont = useCallback(() => setFontChange((value) => value - 1), []);
  const [rescrapeMessage, setRescrapeMessage] = useState<string | null>(null);



  // DOM element refs for scroll sync and fullscreen
  const lyricsRef = useRef<HTMLDivElement>(null);
  const lyricsCardRef = useRef<HTMLDivElement>(null);
  const paneRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollOffsetsRef = useRef<number[]>([0]);
  const baseScrollPosRef = useRef<number>(0);
  const unifiedYRef = useRef<number>(0);

  // 1. Song data & saved list status hook
  const { song, isLoading, isHebrew, savedListsInfo, refreshSavedStatus } = useSongLoader({
    songId,
    navigate,
  });

  const pageTitle = useMemo(() => {
    if (isLoading || !song) return DEFAULT_PAGE_TITLE;
    return formatSongTitle(song.artist, song.title);
  }, [isLoading, song]);

  useDocumentTitle(pageTitle);

  const visibleBody = useMemo(() => song?.body?.map((row) => ({
    ...row,
    chords: visibleContent.includes("chords") ? row.chords : undefined,
    text: visibleContent.includes("lyrics") ? row.text : undefined,
    tab: visibleContent.includes("tabs") ? row.tab : undefined,
  })).filter((row) => row.header || row.chords || row.text || row.tab?.length), [song?.body, visibleContent]);

  // 2. Fullscreen hook
  const { isFullscreen, toggleFullscreen } = useSongFullscreen(lyricsCardRef);

  // 3. Split view pagination hook
  const {
    splitMode,
    preferredSplitMode,
    effectiveSplitMode,
    splitPageState,
    totalPages,
    handleSplitModeChange,
    handleNextPage,
    handlePrevPage,
  } = useSongPagination({
    body: visibleBody,
    paneRefs,
    lyricsRef,
    fontChange,
    isFullscreen,
    scrollOffsetsRef,
    baseScrollPosRef,
    unifiedYRef,
  });

  // 4. Auto-scroll & synchronized unified scrolling hook
  const {
    isScrolling,
    scrollSpeed,
    mobileSettingsOpen,
    setMobileSettingsOpen,
    toggleScroll,
    handleSpeedChange,
    handlePaneScroll,
    applyUnifiedScrollDelta,
  } = useSongAutoScroll({
    paneRefs,
    lyricsRef,
    splitMode: effectiveSplitMode,
    scrollOffsetsRef,
    baseScrollPosRef,
    unifiedYRef,
  });

  // 5. Centralized keyboard shortcuts
  useKeyboardShortcuts({
    onSplit1: () => handleSplitModeChange(null, 1),
    onSplit2: () => handleSplitModeChange(null, 2),
    onSplit3: () => handleSplitModeChange(null, 3),
    onIncreaseFont: increaseFont,
    onDecreaseFont: decreaseFont,
    onToggleFullscreen: toggleFullscreen,
    onToggleAutoScroll: toggleScroll,
    onSlowerSpeed: () => handleSpeedChange(null, Math.max(0.01, scrollSpeed[0] - 0.05)),
    onFasterSpeed: () => handleSpeedChange(null, Math.min(1.0, scrollSpeed[0] + 0.05)),
    onNextPage: handleNextPage,
    onPrevPage: handlePrevPage,
    onOpenShortcutsModal: () => setShortcutsModalOpen(true),
  });

  const copyChords = useCallback(() => {
    if (!song?.body) return;
    const text = song.body
      .map(
        (row) =>
          (row.header ? row.header + "\n" : "") +
          (row.chords ? row.chords + "\n" : "") +
          (row.text ? row.text + "\n" : "") +
          (row.tab ? row.tab.join("\n") + "\n" : "") +
          "\n"
      )
      .join("");
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [song?.body]);

  const handleRescrape = useCallback(async () => {
    if (!song?.url) return;
    try {
      await apiScrapeUrl(song.url);
      setRescrapeMessage("Re-scraped successfully.");
      if (song.id) refreshSavedStatus(song.id);
    } catch {
      setRescrapeMessage("Re-scrape failed.");
    }
  }, [song?.url, song?.id, refreshSavedStatus]);

  const { user } = useUser();
  const isOwner = Boolean(
    user && song && song.owner && (song.owner === user.id || song.owner === user.email)
  );

  const handleEdit = useCallback(() => {
    if (!song) return;
    if (isOwner) {
      navigate(`/EditSong?id=${song.id}`);
    } else {
      navigate(`/EditSong?cloneId=${song.id}`);
    }
  }, [song, isOwner, navigate]);

  if (isLoading || !song) {
    return (
      <Box p={4} textAlign="center" bgcolor="#ffffff" minHeight="100vh">
        <Typography variant="h6" color="text.secondary">
          Loading song...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 0.5, sm: 1 },
        py: { xs: 0.5, sm: 1 },
        pb: 0,
        bgcolor: "#ffffff",
        minHeight: "100%",
        flex: 1,
      }}
    >
      <Container maxWidth="xl" disableGutters sx={{ px: 0 }}>
        <SongHeader
          title={song.title}
          artist={song.artist}
          savedListsCount={savedListsInfo.length}
          isOwner={isOwner}
          onBack={() => navigate("/Explore")}
          onOpenShortcuts={() => setShortcutsModalOpen(true)}
          onOpenSaveModal={() => setSaveModalOpen(true)}
          onRescrape={song.url ? handleRescrape : undefined}
          onEdit={handleEdit}
        />

        {/* Saved Lists & Notes */}
        <SongSavedListsBar
          savedListsInfo={savedListsInfo}
          onEditNotes={() => setSaveModalOpen(true)}
        />

        {/* Song Metadata Chips */}
        <SongMetadataChips song={song} />

        {/* Song Split View & Controls */}
        <SongSplitView
          song={{ ...song, body: visibleBody ?? [] }}
          visibleContent={visibleContent}
          onVisibleContentChange={setVisibleContent}
          fontChange={fontChange}
          isHebrew={isHebrew}
          isScrolling={isScrolling}
          scrollSpeed={scrollSpeed}
          mobileSettingsOpen={mobileSettingsOpen}
          splitMode={splitMode}
          preferredSplitMode={preferredSplitMode}
          splitPageState={splitPageState}
          totalPages={totalPages}
          isFullscreen={isFullscreen}
          lyricsCardRef={lyricsCardRef}
          lyricsRef={lyricsRef}
          paneRefs={paneRefs}
          onToggleScroll={toggleScroll}
          onToggleMobileSettings={() => setMobileSettingsOpen((prev) => !prev)}
          onSpeedChange={handleSpeedChange}
          onSplitModeChange={handleSplitModeChange}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
          onIncreaseFont={increaseFont}
          onDecreaseFont={decreaseFont}
          onCopyChords={copyChords}
          onToggleFullscreen={toggleFullscreen}
          onPaneScroll={handlePaneScroll}
          applyUnifiedScrollDelta={applyUnifiedScrollDelta}
          showSplitIndicators={showSplitIndicators}
          onToggleSplitIndicators={toggleSplitIndicators}
        />

        <Snackbar
          open={copied}
          message="Chords copied to clipboard!"
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={() => setCopied(false)}
          autoHideDuration={1500}
        />

        <Snackbar
          open={!!rescrapeMessage}
          message={rescrapeMessage}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={() => setRescrapeMessage(null)}
          autoHideDuration={2500}
        />

        {/* Save to List Modal */}
        {song && song.id > 0 && saveModalOpen && (
          <SaveToListModal
            open={saveModalOpen}
            onClose={() => {
              setSaveModalOpen(false);
              if (song.id) refreshSavedStatus(song.id);
            }}
            song={song}
          />
        )}

        {/* Keyboard Shortcuts Cheatsheet Modal */}
        <ShortcutsModal
          open={shortcutsModalOpen}
          onClose={() => setShortcutsModalOpen(false)}
        />
      </Container>
    </Box>
  );
}
